/// <amd-module name="WorkItemsHub/Utils/Telemetry" />

// import * as Diag from "VSS/Diag";
import { IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";
import { IVssPageContext } from 'VSS/Platform/Context';
import { IVssTelemetryService } from 'VSS/Platform/Telemetry';
export namespace TelemetryConstants {
    export const Area = "WorkItemsHub";

    // Performance scenarios
    export const WorkItemsHubInitialLoad = "WorkItemsHubInitialLoad";
    export const WorkItemsHubTabSwitch = "WorkItemsHubTabSwitch";

    // Usage scenarios
    export const CreateWorkItem = "CreateWorkItem";
    export const OpenInQueries = "OpenInQueries";
    export const ColumnOptionsCommand = "ColumnOptionsCommand";
    export const RecycleBin = "RecycleBin";
    export const ImportWorkItems = "ImportWorkItems";

    export const CopyToClipboard = "CopyToClipboard";
    export const Email = "Email";
    export const Delete = "Delete";
    export const DeleteCommand = "DeleteCommand";
    export const CompletedWorkItems = "CompletedWorkItems";
    export const ColumnOptionsChange = "ColumnOptionsChange";
    export const ColumnResize = "ColumnResize";
    export const ColumnHeaderClick = "ColumnHeaderClick";
    export const BackCompatOpenWorkItem = "BackCompatOpenWorkItem";
    export const BackCompatQuery = "BackCompatQuery";
    export const LoadWorkItemsHub = "LoadWorkItemsHub";
    export const ClickOnWorkItem = "ClickOnWorkItem";
    export const ChangedTabValue = "ChangedTabValue";
}

enum PerformanceScenarioType {
    Unknown,
    PageLoad,
    TabSwitch
}

/**
 * Telemetry helper for work items hub performance scenarios
 */
export class PerformanceTelemetryHelper {

    private static _instance: PerformanceTelemetryHelper;

    private _scenarioType: PerformanceScenarioType;
    private _service: IVssPerformanceService;

    private constructor(pageContext: IVssPageContext) {
        this._service = pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public static getInstance(pageContext: IVssPageContext): PerformanceTelemetryHelper {
        if (!PerformanceTelemetryHelper._instance) {
            PerformanceTelemetryHelper._instance = new PerformanceTelemetryHelper(pageContext);
        }
        return PerformanceTelemetryHelper._instance;
    }

    public startPageLoad() {
        this._scenarioType = PerformanceScenarioType.PageLoad;
    }

    public startTabSwitch() {
        this._scenarioType = PerformanceScenarioType.TabSwitch;
        this._service.startScenario(TelemetryConstants.WorkItemsHubTabSwitch, false);
    }

    public endScenario(additionalProperties?: { [key: string]: any; }) {
        switch (this._scenarioType) {
            case PerformanceScenarioType.TabSwitch:
                this._service.endScenario(TelemetryConstants.Area, TelemetryConstants.WorkItemsHubTabSwitch, false, undefined, additionalProperties);
                break;

            case PerformanceScenarioType.PageLoad:
                this._service.markTimeToInteractive(TelemetryConstants.Area, TelemetryConstants.WorkItemsHubInitialLoad, undefined, additionalProperties);
                break;
        }

        this._scenarioType = PerformanceScenarioType.Unknown;
    }

    public split(timingName: string) {
        this._service.addSplitTiming(timingName);
    }
}

/**
 * Helper for work items hub usage telemetry
 */
export namespace UsageTelemetryHelper {
    /**
     * Publish telemetry for Create New Work Item command
     * @param workItemType The work item type
     */
    export function publishCreateNewWorkItemTelemetry(pageContext: IVssPageContext, workItemType: string): void {
        _publish(pageContext, TelemetryConstants.CreateWorkItem, { workItemType });
    }

    /**
     * Publish telemetry for Import Work Items command
     * @param tabId Tab id
     */
    export function publishImportWorkItemsCommand(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.ImportWorkItems, { tabId });
    }

    /**
     * Publish telemetry for RecycleBin command
     * @param tabId Tab id
     */
    export function publishRecycleBinCommand(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.RecycleBin, { tabId });
    }

    /**
     * Publish telemetry for Column Options command
     * @param tabId Tab id
     */
    export function publishColumnOptionsCommandpageContext(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.ColumnOptionsCommand, { tabId });
    }

    /**
     * Publish telemetry for Open in Queries command
     * @param tabId Tab id
     * @param hasFilter Indicate if filter applied
     * @param hasSelection Indicate if selection applied
     */
    export function publishOpenInQueriesTelemetry(pageContext: IVssPageContext, tabId: string, hasFilter: boolean, hasSelection: boolean): void {
        _publish(pageContext, TelemetryConstants.OpenInQueries, { tabId, hasFilter, hasSelection });
    }

    /**
     * Publish telemetry for Copy to clipboard command
     * @param tabId Tab id
     * @param keyboardShortcutEvent Indicates if the command was triggered via keyboard shortcut
     */
    export function publishCopyToClipboardTelemetry(pageContext: IVssPageContext, tabId: string, keyboardShortcutEvent: boolean): void {
        _publish(pageContext, TelemetryConstants.CopyToClipboard, { tabId, keyboardShortcutEvent });
    }

    /**
     * Publish telemetry for Delete command
     * @param tabId Tab id
     * @param keyboardShortcutEvent Indicates if the command was triggered via keyboard shortcut
     */
    export function publishDeleteTelemetry(pageContext: IVssPageContext, tabId: string, keyboardShortcutEvent: boolean): void {
        _publish(pageContext, TelemetryConstants.Delete, { tabId, keyboardShortcutEvent });
    }

    /**
     * Publish telemetry for Email command
     * @param tabId Tab id
     */
    export function publishEmailTelemetry(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.Email, { tabId });
    }

    /**
     * Publish telemetry for view option (completed work items) change
     * @param tabId Tab id
     * @param isOn Whether the view option is turned on or not
     */
    export function publishCompletedWorkItemsViewOptionTelemetry(pageContext: IVssPageContext, tabId: string, isOn: boolean): void {
        _publish(pageContext, TelemetryConstants.CompletedWorkItems, { tabId, isOn });
    }

    /**
     * Publish telemetry for column options change
     * @param tabId Tab id
     * @param properties properties
     */
    export function publishColumnOptionsChange(pageContext: IVssPageContext, tabId: string, properties: { [key: string]: string | number | boolean }): void {
        _publish(pageContext, TelemetryConstants.ColumnOptionsChange, { tabId, ...properties });
    }

    /**
     * Publish telemetry for Column Options command
     * @param tabId Tab id
     */
    export function publishColumnOptionsCommand(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.ColumnOptionsCommand, { tabId });
    }

    /**
     * Publish telemetry for column resize
     * @param tabId Tab id
     * @param properties properties
     */
    export function publishColumnResize(pageContext: IVssPageContext, tabId: string, properties: { [key: string]: string | number | boolean }): void {
        _publish(pageContext, TelemetryConstants.ColumnResize, { tabId, ...properties });
    }

    /**
     * Publish telemetry for column header click
     * @param tabId Tab id
     * @param properties properties
     */
    export function publishColumnHeaderClick(pageContext: IVssPageContext, tabId: string, properties: { [key: string]: string | number | boolean }): void {
        _publish(pageContext, TelemetryConstants.ColumnHeaderClick, { tabId, ...properties });
    }

    /**
     * Publish back compat url usage (open work item with hash)
     */
    export function publishBackCompatUrlOpenWorkItem(pageContext: IVssPageContext, hash: string): void {
        _publish(pageContext, TelemetryConstants.BackCompatOpenWorkItem, { hash });
    }

    /**
     * Publish back compat url usage (query with hash)
     */
    export function publishBackCompatUrlQuery(pageContext: IVssPageContext, hash: string): void {
        _publish(pageContext, TelemetryConstants.BackCompatQuery, { hash });
    }

    export function publishLoadWorkItemsHub(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.LoadWorkItemsHub, { tabId });
    }

    export function publishClickOnWorkItem(pageContext: IVssPageContext, workItemId: number): void {
        _publish(pageContext, TelemetryConstants.ClickOnWorkItem, { workItemId });
    }

    export function publishChangedTabValue(pageContext: IVssPageContext, tabId: string): void {
        _publish(pageContext, TelemetryConstants.ChangedTabValue, { tabId });
    }

    function _publish(pageContext: IVssPageContext, featureName: string, properties: { [key: string]: string | number | boolean }): void {

        const service = pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        service.publishEvent(TelemetryConstants.Area, featureName, properties);
    }
}
